import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Store, Search } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { mapPostRow } from '@/lib/mapPost'
import PostCard from '@/components/features/feed/PostCard'
import SearchBar from '@/components/ui/SearchBar'
import FollowedBusinessesList from '@/components/features/business/FollowedBusinessesList'
import { useFollowedBusinesses } from '@/hooks/useBusiness'
import { useMyInterests, useSavedPosts } from '@/hooks/useFeed'
import { useHorizontalScroll } from '@/hooks/useHorizontalScroll'
import { STRIPE, type PostData } from '@/data/mockData'

const C = {
  teal: '#0F5C5C',
  ink: '#202523',
  ink2: '#6D7471',
  border: '#E1E5E3',
  grayBg: '#F1F3F2',
}
const FH = "'Plus Jakarta Sans', sans-serif"
const FB = "'Inter', sans-serif"

interface FollowedBusiness {
  id: string
  name: string
  category: string
  zone: string
  emoji: string
  cover_colors: [string, string]
  verified: boolean
  is_open: boolean
  follower_count: number
}

export default function FollowingPage() {
  const navigate = useNavigate()
  const [tab, setTab] = useState<'posts' | 'business'>('posts')
  const [bizQuery, setBizQuery] = useState('')
  const avatarsScrollRef = useHorizontalScroll<HTMLDivElement>()

  const { data: followedData, isLoading: loadingBiz } = useFollowedBusinesses()
  const businesses = (followedData?.data ?? []) as unknown as FollowedBusiness[]
  const bizIds = businesses.map((b) => b.id)

  const myInterests = useMyInterests()
  const { data: savedData } = useSavedPosts()
  const mySavedIds = new Set((savedData?.data ?? []).map((p) => p.id as string))

  const { data: postsData, isLoading: loadingPosts } = useQuery({
    queryKey: ['following-feed', bizIds],
    queryFn: async () => {
      if (!supabase || bizIds.length === 0) return []
      const { data } = await supabase
        .from('posts')
        .select('*')
        .eq('is_active', true)
        .in('business_id', bizIds)
        .order('created_at', { ascending: false })
      return data ?? []
    },
    enabled: bizIds.length > 0,
    staleTime: 1000 * 30,
  })

  const feed: PostData[] = (postsData ?? []).map((p) =>
    mapPostRow(
      p,
      businesses.find((b) => b.id === p.business_id)
    )
  )

  const title = (
    <div
      style={{
        fontFamily: FH,
        fontWeight: 800,
        fontSize: 26,
        letterSpacing: '-0.01em',
        marginBottom: 22,
      }}
    >
      Siguiendo
    </div>
  )

  // Sin negocios seguidos → estado vacío único
  if (!loadingBiz && businesses.length === 0) {
    return (
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '28px 28px 60px' }}>
        <SearchBar style={{ marginBottom: 20 }} />
        {title}
        <div style={{ textAlign: 'center', padding: '60px 24px' }}>
          <div
            style={{
              width: 68,
              height: 68,
              borderRadius: 18,
              background: C.grayBg,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 16,
            }}
          >
            <Store size={28} color={C.ink2} />
          </div>
          <div style={{ fontFamily: FH, fontWeight: 700, fontSize: 19, marginBottom: 8 }}>
            Sigue negocios para ver sus novedades aquí
          </div>
          <div style={{ fontSize: 14, color: C.ink2, lineHeight: 1.5, marginBottom: 20 }}>
            Cuando sigas a un negocio, sus publicaciones aparecerán en este feed.
          </div>
          <button
            onClick={() => navigate('/')}
            style={{
              background: C.teal,
              color: '#fff',
              border: 'none',
              borderRadius: 11,
              padding: '11px 22px',
              fontFamily: FB,
              fontWeight: 700,
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            Explorar negocios
          </button>
        </div>
      </div>
    )
  }

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '28px 28px 60px' }}>
      <SearchBar style={{ marginBottom: 20 }} />
      {title}

      {/* Fila de avatares de los negocios que sigo */}
      {businesses.length > 0 && (
        <div
          ref={avatarsScrollRef}
          style={{
            display: 'flex',
            gap: 16,
            overflowX: 'auto',
            scrollbarWidth: 'none',
            paddingBottom: 6,
            marginBottom: 22,
          }}
        >
          {businesses.map((b) => (
            <button
              key={b.id}
              onClick={() => navigate(`/biz/${b.id}`)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 6,
                width: 64,
                flexShrink: 0,
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                padding: 0,
              }}
            >
              <div
                style={{
                  width: 58,
                  height: 58,
                  borderRadius: 16,
                  background: STRIPE(b.cover_colors[0], b.cover_colors[1]),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 26,
                }}
              >
                {b.emoji}
              </div>
              <span
                style={{
                  fontSize: 11,
                  color: C.ink2,
                  fontFamily: FB,
                  maxWidth: 64,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {b.name}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Pestañas */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 22 }}>
        {(
          [
            ['posts', 'Publicaciones'],
            ['business', 'Negocios'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            style={{
              padding: '9px 18px',
              borderRadius: 11,
              border: `1px solid ${tab === key ? C.teal : C.border}`,
              background: tab === key ? C.teal : '#fff',
              color: tab === key ? '#fff' : C.ink,
              fontFamily: FB,
              fontWeight: 700,
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'posts' ? (
        loadingBiz || loadingPosts ? null : feed.length === 0 ? (
          <div
            style={{
              padding: '18px 20px',
              borderRadius: 12,
              background: '#fff',
              border: `1px solid ${C.border}`,
              color: C.ink2,
              fontSize: 13.5,
            }}
          >
            Los negocios que sigues no tienen publicaciones activas por ahora.
          </div>
        ) : (
          <div className="feed-grid-3">
            {feed.map((p) => (
              <PostCard
                key={p.id}
                post={p}
                initialInterested={myInterests.has(p.id)}
                initialSaved={mySavedIds.has(p.id)}
              />
            ))}
          </div>
        )
      ) : (
        <div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              background: '#fff',
              border: `1px solid ${C.border}`,
              borderRadius: 11,
              padding: '10px 14px',
              marginBottom: 16,
              maxWidth: 420,
            }}
          >
            <Search size={17} color={C.ink2} />
            <input
              value={bizQuery}
              onChange={(e) => setBizQuery(e.target.value)}
              placeholder="Busca un negocio que sigues…"
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                fontFamily: FB,
                fontSize: 14,
                color: C.ink,
              }}
            />
          </div>
          <FollowedBusinessesList filter={bizQuery} />
        </div>
      )}
    </div>
  )
}
